import { RouteErrorBoundary } from '../features/error-reporting/views/RouteErrorBoundary';
import AiChatHeaderButton from '../features/ai-chat/views/AiChatHeaderButton';
import AppearanceSwitch from "./AppearanceSwitch";
import AboutModal from './AboutModal';
import AppTheme from './theme/AppTheme';
import { useAppearance } from './contexts/AppearanceContext';
import { canvasBackground } from './theme/canvasBackground';
import { themedShellFor } from './theme/themedShellRoutes';
import ShellPageBody from './theme/ShellPageBody';
import React, { useState, useEffect, Suspense } from 'react';
import {
    BarChartOutlined, MailOutlined, AppstoreOutlined, CodeOutlined,
    FundOutlined, ReconciliationOutlined,
    MenuOutlined, PieChartOutlined,
    SearchOutlined, TeamOutlined, TruckOutlined, WhatsAppOutlined, WalletOutlined,
    UserSwitchOutlined, RobotOutlined, UserOutlined, BankOutlined, ShoppingCartOutlined, SafetyCertificateOutlined, InfoCircleOutlined, SettingOutlined, ControlOutlined, LogoutOutlined
} from '@ant-design/icons';
import { ApprovalCenter } from './approvals/ApprovalCenter';
import { visibleSettingsSections } from '../features/settings/settingsRegistry';
import UserSettingsModal from './SettingsModal/UserSettingsModal';
import { Button, ConfigProvider, Dropdown, Layout, Menu, Spin, theme, Tooltip as AntTooltip } from 'antd';
import { CountBadge as Badge } from './shared/CountBadge';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from './contexts/AuthContext'; // Updated import
import { useCanonicalMessaging } from './contexts/CanonicalMessagingContext';
import { useSocket } from './contexts/SocketContext';
import { StaffAvatar } from './staff/StaffAvatar';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import useSnackbar from "../components/Snackbar/UseSnackbar";
import useLogout from './Login/Logout';
import siteConfig from '../siteConfig';
import uzdLogo from '../assets/images/uzd_logo_small.jpeg';
import InternalChatLauncher from './internalChat/InternalChatLauncher';
import AppSplash from './AppSplash';
import { useAccess } from '../features/access/context/AccessProvider';
import { resolvePageHeader } from '../features/access/constants/pageHeader';
import { PAGE_ROUTES, PERMISSIONS } from '../features/access/constants/permissionKeys';
import { useAccountsNotifications } from './payments-v2/contexts/AccountsNotificationsContext';
import { useMailbox } from './mailbox/MailboxProvider';
import MailboxHeaderButton from './mailbox/MailboxHeaderButton';
import './app-header-actions.css';
import StaffPresenceHeader from './staffPresence/StaffPresenceHeader';
import useStaffPresence from './staffPresence/useStaffPresence';
import { AttendanceProvider, useAttendance } from '../features/attendance/viewmodels/AttendanceProvider';
import { MapsProvider } from '../features/maps/viewmodels/MapsProvider';
import AttendanceGate from '../features/attendance/views/AttendanceGate';
import StatusChip from '../features/attendance/views/StatusChip';
import AttendancePrompt from '../features/attendance/views/AttendancePrompt';
import AttendanceNotifications from '../features/attendance/views/AttendanceNotifications';
import AttendanceExportsProvider from '../features/attendance/views/AttendanceExportsProvider';
import { useSourcingMembership } from '../features/sourcing-team/viewmodels/SourcingProvider';

const { Header, Sider } = Layout;

// Brand block at the top of the sider — logo on a white plate (artwork has a
// white background), wordmark hidden when collapsed
const SiderBrand = ({ collapsed }) => (
    <div style={{
        display: "flex", alignItems: "center", gap: 10,
        height: 56, boxSizing: "border-box",
        padding: collapsed ? "0" : "0 16px",
        justifyContent: collapsed ? "center" : "flex-start",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        marginBottom: 8,
    }}>
        <div style={{
            background: "#fff", borderRadius: 8, padding: "4px 6px", flexShrink: 0,
            boxShadow: "0 2px 10px rgba(225,29,46,0.3)",
        }}>
            <img src={uzdLogo} alt="UZD" style={{ height: 18, display: "block", objectFit: "contain" }} />
        </div>
        {!collapsed && (
            <div style={{ lineHeight: 1.15 }}>
                <div style={{ color: "#f1f5f9", fontWeight: 800, fontSize: 13, letterSpacing: 1.5 }}>UZDPART</div>
                <div style={{ color: "#e11d2e", fontWeight: 700, fontSize: 10, letterSpacing: 1.5 }}>Uzdpart360</div>
            </div>
        )}
    </div>
);

function getItem(label, key, icon, path) {
    return { key, icon, label: <Link to={path}>{label}</Link>, path, title: label };
}

if (!document.getElementById("drawer-credit-style")) {
    const s = document.createElement("style");
    s.id = "drawer-credit-style";
    s.textContent = `
        @keyframes credit-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes credit-pulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
    `;
    document.head.appendChild(s);
}

const DrawerNew = () => {
    const { isDark: canvasDark } = useAppearance();
    const canvasStyle = { background: canvasBackground(canvasDark) };
    const sourcing = useSourcingMembership();
    const attendance = useAttendance();
    const mailbox = useMailbox();
    const { handledialogueOpen, LogoutDialog } = useLogout();
    const { ErrorSnackbar, ToastContainer } = useSnackbar();

    // Use the new AuthContext hook
    const { authState, legacyAuthState } = useAuth();
    const { can, status: accessStatus } = useAccess();
    const messaging = useCanonicalMessaging();
    const unreadByAudience = Object.values(messaging.conversations).reduce((counts, conversation) => {
        if (!(conversation.viewer_state?.unread_count > 0)) return counts;
        const audience = messaging.channelAccounts.find(account => account.id === conversation.channel_account_id)?.audience;
        if (audience === "customer" || audience === "mixed") counts.customer += 1;
        if (audience === "supplier" || audience === "mixed") counts.supplier += 1;
        return counts;
    }, { customer: 0, supplier: 0 });
    // The sales pipeline holds supplier chats too, for those who can see them.
    const qv4TotalUnread = unreadByAudience.customer + (can(PERMISSIONS.SUPPLIER_CHATS_VIEW) ? unreadByAudience.supplier : 0);
    const { socket, isConnected, isNetworkOnline, watiCreditBalance: templateCredit, watiCreditLoading: creditLoading, refreshWatiCredit } = useSocket();
    const { pendingCount: accountsPendingCount } = useAccountsNotifications();

    const [userSettingsOpen, setUserSettingsOpen] = useState(false);
    const [aboutOpen, setAboutOpen] = useState(false);
    const [pageDrawerWidth, setPageDrawerWidth] = useState(0);
    const location = useLocation();
    const isThemedPage = Boolean(themedShellFor(location.pathname));
    const presenceChannel = location.pathname.startsWith('/whatsapp_messages') && can(PERMISSIONS.SUPPLIER_CHATS_VIEW)
      ? 'supplier_support' : can(PERMISSIONS.SALES_VIEW) ? 'customer_support'
        : can(PERMISSIONS.SUPPLIER_CHATS_VIEW) ? 'supplier_support' : null;
    const [presenceOpen, setPresenceOpen] = useState(false);
    useEffect(() => setPresenceOpen(false), [authState?.id, authState?.session_id, presenceChannel]);
    const staffPresence = useStaffPresence({ socket, channel: presenceChannel, actorId: authState?.id,
      token: authState?.token, sessionId: authState?.session_id, openPresence: () => setPresenceOpen(true) });
    const navigate = useNavigate();
    const isQV4 = location.pathname === '/enquiries-for-me' || location.pathname === '/internal-chat' || location.pathname.startsWith('/settings') || location.pathname.startsWith('/mail') || location.pathname === "/" || location.pathname.startsWith("/querysetv4") || location.pathname.startsWith("/operations-v2") || location.pathname === "/my-purchases" || location.pathname === "/my-deliveries" || location.pathname === "/ai_assistant";

    // For backward compatibility, use legacyAuthState if available
    const userState = legacyAuthState || authState;


    // Define menu items - use isAuthenticated from new authState
    const pageIcons = {
        mail: <MailOutlined />,
        dashboard: <PieChartOutlined />, "legacy-sales": <WhatsAppOutlined />,
        sales: <FundOutlined />,
        operations: <ReconciliationOutlined />, "my-purchases": <ShoppingCartOutlined />, "my-deliveries": <TruckOutlined />, accounts: <BankOutlined />, ai: <RobotOutlined />, suppliers: <TruckOutlined />,
        users: <TeamOutlined />, "access-management": <SafetyCertificateOutlined />, reports: <BarChartOutlined />, "price-search": <SearchOutlined />, customers: <UserOutlined />,
    };
    pageIcons['reports-overview'] = <BarChartOutlined />;
    pageIcons['account-approvals'] = <UserSwitchOutlined />;
    pageIcons['wati-diagnostics'] = <WhatsAppOutlined />;
    pageIcons['developer-tools'] = <CodeOutlined />;
    const navIcons = { ...pageIcons };
    for (const [key, count] of Object.entries({ sales: qv4TotalUnread, accounts: accountsPendingCount })) {
        navIcons[key] = <Badge count={count} size="small" offset={[10, 10]}>{pageIcons[key]}</Badge>;
    }
    const items = authState.isAuthenticated && accessStatus === "ready"
        ? PAGE_ROUTES.filter(route => route.nav && route.key !== 'mail' && can(route.permission)).map(route => getItem(route.label, route.key, navIcons[route.key], route.path))
        : [];
    if (authState.isAuthenticated && sourcing.state !== 'loading' && sourcing.state !== 'nonmember') items.push(getItem('Enquiries for me', 'sourcing-inbox', <Badge count={sourcing.total} size="small"><ShoppingCartOutlined /></Badge>, '/enquiries-for-me'));

    const isMobile = useIsMobile();
    const [collapsed, setCollapsed] = useState(() => window.innerWidth < 768);
    const currentPage = resolvePageHeader(location.pathname);
    const selectedKey = location.pathname === '/enquiries-for-me' ? 'sourcing-inbox' : currentPage?.nav ? currentPage.key : (currentPage?.parentKey || (currentPage?.key === 'reports-overview' ? 'reports' : ''));
    const currentHeader = location.pathname === '/enquiries-for-me' ? 'Enquiries for me' : currentPage?.label || (location.pathname === '/access-denied' ? 'Access denied' : 'Page not found');

    // Auto-collapse on desktop after 2.5s; mobile starts collapsed already
    useEffect(() => {
        if (isMobile) return;
        const t = setTimeout(() => setCollapsed(true), 2500);
        return () => clearTimeout(t);
    }, [isMobile]);

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Don't show loading spinner here - auth is handled by App
    if (!authState.isAuthenticated) {
        return null; // Or a redirect component
    }
    if (accessStatus === "loading" || accessStatus === "idle") return <AppSplash />;

    const dropdown_items = [
        {
            key: '5',
            label: "User Settings",
            icon: <SettingOutlined />,
        },
        ...(visibleSettingsSections(can).length ? [{
            key: '4',
            label: "Global Settings",
            icon: <ControlOutlined />,
        }] : []),
        { key: 'about', label: 'About', icon: <InfoCircleOutlined /> },
        {
            key: '3',
            label: "Logout",
            icon: <LogoutOutlined />,
        },
    ];

    const onMenuClick = (e) => {
        switch (e.key) {
            case 'about':
                setAboutOpen(true);
                break;
            case "5":
                setUserSettingsOpen(true);
                break;
            case "3":
                handledialogueOpen();
                break;
            case "4":
                navigate('/settings');
                break;
            default:
                ErrorSnackbar(`Invalid option selected!`);
                break;
        }
    };

    return (
        <ConfigProvider theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
                borderRadius: 8,
                colorBgLayout: "#f4f5f7",
                colorBorderSecondary: "#eceef1",
                boxShadowSecondary: "0 4px 16px rgba(15, 23, 42, 0.08), 0 1px 4px rgba(15, 23, 42, 0.05)",
            },
            components: {
                Layout: {
                    siderBg: "#0e1013",
                    triggerBg: "#16181d",
                    triggerColor: "rgba(255,255,255,0.55)",
                },
                Button: { fontWeight: 500 },
                Card: { borderRadiusLG: 12 },
                Modal: { borderRadiusLG: 14 },
                Drawer: { borderRadiusLG: 14 },
                Table: { headerBg: "#f7f8fa", headerSplitColor: "transparent" },
                Tag: { borderRadiusSM: 6 },
                Collapse: { headerBg: "#fafbfc", borderRadiusLG: 10 },
                Notification: { borderRadiusLG: 12 },
                Menu: {
                    darkItemBg: "#0e1013",
                    darkSubMenuItemBg: "#0e1013",
                    darkPopupBg: "#16181d",
                    darkItemColor: "rgba(255,255,255,0.62)",
                    darkItemHoverBg: "rgba(255,255,255,0.07)",
                    darkItemHoverColor: "#ffffff",
                    darkItemSelectedBg: "#e11d2e",
                    darkItemSelectedColor: "#ffffff",
                    itemBorderRadius: 8,
                    itemMarginInline: 8,
                    itemHeight: 42,
                },
            },
        }}>
            <Layout style={isQV4 ? { ...canvasStyle, height: '100vh', width: '100vw', overflow: 'hidden' } : { ...canvasStyle, minHeight: '100vh', minWidth: '100vw' }}>
                <AppTheme>{staffPresence.noticeHolder}</AppTheme>

                {/* ── Desktop: sticky collapsible sider ── */}
                {!isMobile && (
                    <Sider
                        collapsible
                        collapsed={collapsed}
                        onCollapse={setCollapsed}
                        style={{ overflow: "auto", height: "100vh", position: "sticky", top: 0, left: 0 }}
                    >
                        <SiderBrand collapsed={collapsed} />
                        <Menu theme="dark" selectedKeys={[selectedKey]} defaultOpenKeys={['pipeline-group']} mode="inline" items={items} />
                    </Sider>
                )}

                {/* ── Mobile: full-screen overlay when open ── */}
                {isMobile && !collapsed && (
                    <>
                        <div
                            onClick={() => setCollapsed(true)}
                            style={{
                                position: "fixed", inset: 0,
                                background: "rgba(0,0,0,0.5)",
                                zIndex: 1000,
                            }}
                        />
                        <Sider
                            width={230}
                            style={{
                                position: "fixed", top: 0, left: 0,
                                height: "100dvh", zIndex: 1001,
                                overflow: "auto",
                            }}
                        >
                            <SiderBrand collapsed={false} />
                            <Menu
                                theme="dark"
                                selectedKeys={[selectedKey]}
                                                               mode="inline"
                                items={items}
                                onClick={() => setCollapsed(true)}
                            />
                        </Sider>
                    </>
                )}

                <Layout style={isQV4 ? { ...canvasStyle, flex: 1, overflow: 'hidden', minHeight: 0 } : canvasStyle}>
                    <div className='sticky top-0 left-0 z-99'>
                        <Header
                            className={location.pathname.startsWith('/settings') ? 'settings-shell-header' : undefined}
                            style={{
                                padding: isMobile ? "0 10px" : "0 20px",
                                width: "100%",
                                height: 56,
                                lineHeight: "56px",
                                background: "#0e1013",
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: isQV4 || isThemedPage ? 0 : 12,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                {isMobile && (
                                    <Button
                                        type="text"
                                        icon={<MenuOutlined style={{ fontSize: 18 }} />}
                                        onClick={() => setCollapsed(false)}
                                        style={{ color: "white" }}
                                    />
                                )}
                                <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", fontSize: 20, color: "#e11d2e", flexShrink: 0 }}>
                                    {pageIcons[currentPage?.key] || (location.pathname === '/access-denied' ? <SafetyCertificateOutlined /> : <AppstoreOutlined />)}
                                </span>
                                <h1 style={{
                                    margin: 0, fontSize: isMobile ? 15 : 17, fontWeight: 700,
                                    letterSpacing: 0.3, whiteSpace: "nowrap", overflow: "hidden",
                                    textOverflow: "ellipsis", color: "#f1f5f9",
                                }}>
                                    {currentHeader}
                                </h1>
                            </div>
                            <div className="app-header-actions" role="group" aria-label="Application header actions" style={{ display: "flex", alignItems: "center", gap: 12, marginRight: pageDrawerWidth, transition: "margin-right 0.15s" }}>
                                <AppearanceSwitch /><AiChatHeaderButton />
                                <MailboxHeaderButton visible={!attendance?.gateActive && can(PERMISSIONS.MAILBOX_VIEW)} inboxUnread={mailbox?.inboxUnread} />
                                {!attendance?.gateActive && can(PERMISSIONS.INTERNAL_CHAT_USE) && Number(authState?.user?.internal_chat_enabled) === 1 && <InternalChatLauncher />}
                                {(() => {
                                    const creditColor = templateCredit === null ? "#8c8c8c" : templateCredit >= 10 ? "#52c41a" : templateCredit >= 5 ? "#faad14" : "#ff4d4f";
                                    const creditBg   = templateCredit === null ? "rgba(140,140,140,0.12)" : templateCredit >= 10 ? "rgba(82,196,26,0.12)" : templateCredit >= 5 ? "rgba(250,173,20,0.12)" : "rgba(255,77,79,0.12)";
                                    return (
                                        <AntTooltip title="Template credit balance" placement="bottom">
                                            <div
                                            className="app-header-wallet"
                                            onClick={refreshWatiCredit}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 5,
                                                cursor: creditLoading ? "wait" : "pointer",
                                                backgroundColor: creditBg,
                                                border: `1.5px solid ${creditColor}`,
                                                borderRadius: 8,
                                                alignSelf: "stretch",
                                                padding: "0 12px 0 10px",
                                                opacity: creditLoading ? 0.6 : 1,
                                                animation: creditLoading ? "credit-pulse 0.8s ease-in-out infinite" : "none",
                                                transition: "opacity 0.2s",
                                            }}>
                                                <WalletOutlined style={{
                                                    fontSize: 14,
                                                    color: creditColor,
                                                    animation: creditLoading ? "credit-spin 1s linear infinite" : "none",
                                                }} />
                                                <span style={{ fontSize: 12, fontWeight: 700, color: creditColor, lineHeight: 1 }}>
                                                    {templateCredit === null ? "—" : `$${templateCredit.toFixed(2)}`}
                                                </span>
                                            </div>
                                        </AntTooltip>
                                    );
                                })()}
                                {!attendance?.gateActive && presenceChannel && <StaffPresenceHeader connected={isConnected} networkOnline={isNetworkOnline} roster={staffPresence.snapshot} error={staffPresence.error} open={presenceOpen} onOpenChange={setPresenceOpen} />}
                                <StatusChip />
                                {!attendance?.gateActive && can(PERMISSIONS.APPROVALS_VIEW) && <ApprovalCenter />}
                                <AppTheme><Dropdown
                                    className='cursor-pointer'
                                    menu={{ items: dropdown_items, onClick: onMenuClick }}
                                    placement="bottomLeft"
                                >
                                    <AntTooltip placement='left' title={`Logged in as ${userState?.user?.name || "Unknown User"}`}>
                                        <StaffAvatar
                                        size={34}
                                        userId={userState?.user?.id ?? authState?.id}
                                        name={[userState?.user?.name, userState?.user?.surname].filter(Boolean).join(" ") || "Unknown User"}
                                        pfp={userState?.user?.pfp}
                                        style={{ backgroundColor: '#fde3cf', color: '#f56a00', borderRadius: 8, border: '1px solid #30363e', cursor: 'pointer', flexShrink: 0 }}
                                    />
                                    </AntTooltip>
                                </Dropdown></AppTheme>
                            </div>
                        </Header>
                    </div>
                    <ShellPageBody pathname={location.pathname} isQV4={isQV4} isMobile={isMobile} colorBgContainer={colorBgContainer} borderRadiusLG={borderRadiusLG} legacyFooterColor="#8c8c8c"
                        footer={<>Uzdpart Spare Parts Trading LLC © {new Date().getFullYear()} · {siteConfig.name} v{siteConfig.version} · build {siteConfig.build_no}</>}>
                            {/* Inner boundary: page-chunk loads keep the nav shell visible */}
                            <Suspense fallback={
                                <div style={{ height: "100%", minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Spin size="large" />
                                </div>
                            }>
                                <RouteErrorBoundary key={location.pathname}><AttendanceGate><AttendancePrompt /><Outlet context={{ setPageDrawerWidth }} /></AttendanceGate></RouteErrorBoundary>
                            </Suspense>
                    </ShellPageBody>
                </Layout>
            </Layout>
            {LogoutDialog}
            <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
            <UserSettingsModal
                open_settings={userSettingsOpen}
                handleCloseSettingsModal={() => setUserSettingsOpen(false)}
                authState={authState}
            />
            <ToastContainer />
        </ConfigProvider>
    );
};

export default function AttendanceShell() {
    const { authState } = useAuth();
    return <MapsProvider><AttendanceProvider key={`${authState?.id}:${authState?.token}:${authState?.session_id}`}><AttendanceExportsProvider><AttendanceNotifications /><DrawerNew /></AttendanceExportsProvider></AttendanceProvider></MapsProvider>;
}
